import React from 'react';
import { Clock, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';

/**
 * Componente para exibir indicador visual de SLA
 * 
 * Regras:
 * - Verde: Mais de 3 dias até vencer
 * - Amarelo: 1-3 dias para vencer  
 * - Vermelho: Vencido
 * - Cinza: Finalizado/Sem SLA
 */
export function SLAIndicator({ ticket, compact = false }) {
  // Se ticket foi finalizado ou cancelado
  if (!ticket || ['SENT_TO_CLIENT', 'FINALIZED', 'CANCELLED'].includes(ticket.status)) {
    if (compact) return null;
    return (
      <div className="flex items-center gap-1.5 text-slate-400">
        <CheckCircle className="w-4 h-4" />
        <span className="text-xs">Concluído</span>
      </div>
    );
  }

  // Se não tem SLA definido (ticket ativo sem prazo)
  if (!ticket.slaDueAt) {
    if (compact) return null;
    return (
      <div className="flex items-center gap-1.5 text-slate-500">
        <Clock className="w-4 h-4" />
        <span className="text-xs">Sem prazo</span>
      </div>
    );
  }

  const now = new Date();
  const deadline = new Date(ticket.slaDueAt);
  const diffMs = deadline - now;
  const daysRemaining = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  let status, color, bgColor, icon, label;

  if (daysRemaining > 3) {
    // Verde - Dentro do prazo
    status = 'OK';
    color = 'text-emerald-700';
    bgColor = 'bg-emerald-50';
    icon = <Clock className="w-4 h-4" />;
    label = `${daysRemaining} dias`;
  } else if (daysRemaining >= 1) {
    // Amarelo - Vencendo
    status = 'WARNING';
    color = 'text-amber-700';
    bgColor = 'bg-amber-50';
    icon = <AlertTriangle className="w-4 h-4" />;
    label = `${daysRemaining} dia${daysRemaining > 1 ? 's' : ''}`;
  } else if (daysRemaining === 0) {
    // Amarelo - Vence hoje
    status = 'WARNING';
    color = 'text-amber-700';
    bgColor = 'bg-amber-50';
    icon = <AlertTriangle className="w-4 h-4" />;
    label = 'Vence hoje';
  } else {
    // Vermelho - Vencido
    status = 'OVERDUE';
    color = 'text-red-700';
    bgColor = 'bg-red-50';
    icon = <XCircle className="w-4 h-4" />;
    const daysOverdue = Math.abs(daysRemaining);
    label = `Vencido há ${daysOverdue} dia${daysOverdue > 1 ? 's' : ''}`;
  }

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded ${bgColor} ${color}`}>
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
    );
  }

  return (
    <div className="min-w-[140px]">
      <div className="flex items-center gap-1.5 mb-1">
        <div className={color}>{icon}</div>
        <span className={`text-xs font-medium ${color}`}>{label}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
        <div 
          className={`h-full transition-all ${
            status === 'OK' ? 'bg-emerald-500' : 
            status === 'WARNING' ? 'bg-amber-500' : 
            'bg-red-500'
          }`}
          style={{ 
            width: `${Math.min(100, Math.max(0, ((now - new Date(ticket.openedAt)) / (deadline - new Date(ticket.openedAt))) * 100))}%` 
          }}
        />
      </div>
    </div>
  );
}

/**
 * Badge simples de SLA para uso em tabelas
 */
export function SLABadge({ ticket }) {
  // Tickets finalizados
  if (!ticket || ['SENT_TO_CLIENT', 'FINALIZED', 'CANCELLED'].includes(ticket.status)) {
    return <span className="text-xs text-slate-400">-</span>;
  }

  // Tickets sem SLA definido
  if (!ticket.slaDueAt) {
    return <span className="text-xs text-slate-500">Sem prazo</span>;
  }

  const now = new Date();
  const deadline = new Date(ticket.slaDueAt);
  const daysRemaining = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));

  let colorClass, label;

  if (daysRemaining > 3) {
    colorClass = 'bg-emerald-100 text-emerald-700 border-emerald-200';
    label = `🟢 ${daysRemaining}d`;
  } else if (daysRemaining >= 0) {
    colorClass = 'bg-amber-100 text-amber-700 border-amber-200';
    label = `🟡 ${daysRemaining}d`;
  } else {
    const daysOverdue = Math.abs(daysRemaining);
    colorClass = 'bg-red-100 text-red-700 border-red-200';
    label = `🔴 -${daysOverdue}d`;
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
      {label}
    </span>
  );
}

export default SLAIndicator;
